<template>
  <button
    v-if="flagEnabled"
    type="button"
    class="ai-assistant-floating-button"
    data-testid="ai-assistant-floating-button"
    @click="handleClick"
  >
    <span class="ai-assistant-floating-button__mark" aria-hidden="true">
      <svg
        width="20"
        height="20"
        viewBox="0 0 28 29"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.3447 14.0625C22.3447 15.6971 21.8279 17.1154 20.7942 18.3173L22.0923 19.6694L20.3255 21.2379L18.9553 19.8137C17.9818 20.3545 16.9241 20.625 15.7822 20.625C13.9673 20.625 12.4229 19.9939 11.1488 18.7319C9.87481 17.4579 9.23779 15.9014 9.23779 14.0625C9.23779 12.2235 9.87481 10.6731 11.1488 9.41105C12.4229 8.13702 13.9673 7.5 15.7822 7.5C17.5971 7.5 19.1416 8.13702 20.4156 9.41105C21.7017 10.6731 22.3447 12.2235 22.3447 14.0625ZM15.7822 18.1911C16.251 18.1911 16.7197 18.1129 17.1885 17.9567L15.1152 15.7932L16.882 14.2247L19.0995 16.5324C19.6043 15.8113 19.8567 14.988 19.8567 14.0625C19.8567 12.8606 19.4661 11.869 18.6849 11.0877C17.9036 10.3065 16.9361 9.91586 15.7822 9.91586C14.6284 9.91586 13.6609 10.3065 12.8796 11.0877C12.0984 11.869 11.7077 12.8606 11.7077 14.0625C11.7077 15.2524 12.0984 16.238 12.8796 17.0192C13.6609 17.8004 14.6284 18.1911 15.7822 18.1911Z"
          fill="white"
        />
        <path d="M5 7.75391H7.48797V20.3741H5V7.75391Z" fill="white" />
      </svg>
    </span>
    <span class="ai-assistant-floating-button__label">AI Assistant</span>
  </button>
</template>

<script setup>
import { getCurrentInstance, onMounted, ref } from "vue";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { LD_FLAG_IDEA_MDP_599 } from "@components/constants/launchDarklyFlagKeys";

const emit = defineEmits(["click"]);

const app = getCurrentInstance();
const { proxy } = app || {};

/**
 * Kill-switch: renders by default. Set idea-MDP-599 to false in LaunchDarkly
 * to hide the floating button until the Your Stuff screen wires it behind the flag.
 */
const flagEnabled = ref(true);

const handleClick = () => {
  emit("click");

  try {
    const iqOverlayStore = useIQOverlayStore();
    iqOverlayStore.openOverlay();
  } catch {
    // Pinia store is unavailable outside the account app (Storybook / isolated mounts).
  }
};

onMounted(async () => {
  try {
    if (proxy && typeof proxy.$getFlag === "function") {
      flagEnabled.value = await proxy.$getFlag(LD_FLAG_IDEA_MDP_599, true);
    }
  } catch {
    flagEnabled.value = true;
  }
});
</script>

<style scoped>
.ai-assistant-floating-button {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 24px 0 14px;
  border: none;
  border-radius: 9999px;
  background-color: #ed2738;
  cursor: pointer;
  box-shadow: 0px 4px 16px rgba(16, 24, 40, 0.16);
  transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.ai-assistant-floating-button:hover {
  background-color: #d62330;
  box-shadow: 0px 12px 28px rgba(16, 24, 40, 0.24);
  transform: translateY(-2px);
}

.ai-assistant-floating-button:active {
  background-color: #b91e2c;
  box-shadow: 0px 1px 4px rgba(16, 24, 40, 0.16);
  transform: translateY(0);
}

.ai-assistant-floating-button:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.ai-assistant-floating-button__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.ai-assistant-floating-button__label {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;
  white-space: nowrap;
}
</style>
